/**
 * Alerta de la red (DataOrbit) en el punto de venta.
 *
 * Se dispara sola en cuanto el documento digitado queda completo, en las
 * tres pantallas donde se registra una venta: venta a crédito, venta al
 * contado y solicitud de crédito.
 *
 * Tres reglas, porque esto vive DENTRO del flujo de una venta:
 *
 *   1. Corre EN PARALELO a buscarPorCedula, nunca dentro. El formulario se
 *      llena de inmediato como siempre; el chip aparece cuando llegue. Si
 *      la red tarda o no contesta, el vendedor ni se entera.
 *   2. Nunca rompe nada. El endpoint siempre responde 200 y aquí cualquier
 *      cosa que no sea `disponible` se pinta como nada.
 *   3. No muestra el detalle préstamo por préstamo: cuánto y en cuántos
 *      negocios, nunca con quién. El detalle anonimizado sigue en la
 *      pantalla de DataOrbit, tras su propio permiso.
 *
 * El backend deduplica la auditoría (misma empresa + mismo documento dentro
 * de 10 minutos no escribe fila), así que corregir el documento y volver a
 * salir del campo no ensucia do_consulta.
 */
import React, { useState, useEffect } from "react";
import { Box, Stack, Tooltip, Typography, CircularProgress } from "@mui/material";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import { StatusPill, FP } from "components/financepro";
import { documentoValido } from "lib/documento";
import { postDO, useCreds, SEMAFORO, fmtMoneyDO } from "./common";

// Espera tras la última tecla antes de preguntarle a la red. El documento
// ya tiene que estar completo, así que esto solo absorbe correcciones.
const ESPERA_MS = 600;

/**
 * Consulta la red cuando `cedula` queda completa para su `tipo`.
 * Devuelve { alerta, cargando }; `alerta` es null mientras no haya nada
 * que mostrar.
 */
export function useAlertaRed(cedula, tipo) {
  const creds = useCreds();
  const [alerta, setAlerta] = useState(null);
  const [cargando, setCargando] = useState(false);

  const doc = String(cedula || "").trim();
  const completo = documentoValido(doc, tipo);

  useEffect(() => {
    if (!completo) {
      setAlerta(null);
      setCargando(false);
      return undefined;
    }
    // `vivo` descarta la respuesta de un documento que ya cambió: sin esto
    // una consulta lenta de la cédula anterior podía pisar a la nueva.
    let vivo = true;
    setCargando(true);
    const t = setTimeout(async () => {
      try {
        const { ok, data } = await postDO("/api/dataorbit_alerta_venta", {
          ...creds,
          cedula: doc,
        });
        if (!vivo) return;
        setAlerta(ok && data?.data?.disponible ? data.data : null);
      } catch {
        if (vivo) setAlerta(null); // la venta nunca se entera
      } finally {
        if (vivo) setCargando(false);
      }
    }, ESPERA_MS);

    return () => {
      vivo = false;
      clearTimeout(t);
    };
    // creds viene del contexto y es estable durante la sesión.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, completo]);

  return { alerta, cargando };
}

// Texto corto del chip según lo que trajo la red.
export function resumen(a) {
  if (!a.encontrado) return "Sin antecedentes";
  const partes = [];
  if (a.incobrable) partes.push("Incobrable");
  else if (a.max_dias_atraso > 0) partes.push(`${a.max_dias_atraso} d de atraso`);
  else partes.push("Al día");
  if (a.num_empresas_otras > 0) {
    partes.push(a.num_empresas_otras === 1 ? "1 negocio más" : `${a.num_empresas_otras} negocios más`);
  }
  return partes.join(" · ");
}

// De dónde salió el número. Importa: "vivo" es el estado de ahora mismo;
// "cache" puede venir de un aporte viejo y el cliente ya haber pagado.
const FRESCURA = {
  vivo: "Leído ahora de cada negocio.",
  mixto: "Parte leído ahora, parte del último reporte.",
  cache: "Del último reporte de cada negocio.",
};

function Detalle({ a }) {
  const filas = [
    ["Préstamos en la red", a.num_prestamos],
    ["Negocios", a.num_empresas],
    ["Atraso máximo", `${a.max_dias_atraso} días`],
    ["Cuotas vencidas", a.total_cuotas_vencidas],
    ["Vencido", fmtMoneyDO(a.total_vencido)],
  ];
  return (
    <Box sx={{ py: 0.5 }}>
      {a.es_cliente_propio && (
        <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>
          Ya es cliente de este negocio
        </Typography>
      )}
      {filas.map(([k, v]) => (
        <Stack key={k} direction="row" justifyContent="space-between" spacing={2}>
          <Typography sx={{ fontSize: 12, opacity: 0.8 }}>{k}</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{v}</Typography>
        </Stack>
      ))}
      <Typography sx={{ fontSize: 11, opacity: 0.7, mt: 0.75 }}>
        {FRESCURA[a.fuente] || "El detalle por negocio está en DataOrbit."}
      </Typography>
    </Box>
  );
}

/**
 * Chip de riesgo de la red. Se pinta solo cuando hay algo que decir; el
 * resto del tiempo no ocupa lugar.
 */
export default function AlertaRed({ cedula, tipo, sx = {} }) {
  const { alerta, cargando } = useAlertaRed(cedula, tipo);

  if (cargando) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ height: 22, ...sx }}>
        <CircularProgress size={12} thickness={5} sx={{ color: FP.color.textSecondary }} />
        <Typography sx={{ fontSize: 11, color: FP.color.textSecondary }}>
          Consultando la red…
        </Typography>
      </Stack>
    );
  }
  if (!alerta) return null;

  const semaforo = SEMAFORO[alerta.semaforo] || SEMAFORO.verde;
  // Sin antecedentes el chip es neutro: informa que la consulta corrió, sin
  // vestirse de "aprobado" —la red solo sabe de quien participa en ella—.
  const variant = alerta.encontrado ? semaforo.variant : "neutral";

  return (
    <Tooltip arrow placement="top" title={<Detalle a={alerta} />}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ ...sx }}>
        <HubRoundedIcon sx={{ fontSize: 15, color: FP.color.textSecondary }} />
        <StatusPill variant={variant} label={resumen(alerta)} />
      </Stack>
    </Tooltip>
  );
}
